var _a, _b, _c;
import { __awaiter, __rest } from "tslib";
import './polyfills';
import './index.css';
import './browser-update';
import React from 'react';
import renderApp from 'mage-app';
import { translator } from 'mage-i18n';
import { i18nActions } from 'mage-state';
import PropTypes from 'prop-types';
import { branch, compose, renderComponent } from 'recompose';
import { connect } from 'react-redux';
import withLifecycle from '@hocs/with-lifecycle';
import { BrowserRouter as Router, Route, Switch, withRouter, } from 'react-router-dom';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import _isEmpty from 'lodash/isEmpty';
import { components as coreComponents, selectors as coreSelectors } from 'mage-core';
import { components as clientComponents } from 'mage-client';
import { AccessDenied, GridContainer } from 'mage-components';
import { IllustrationErrorExplodingBalloon, Loader, SpacerVertical } from '@klarna/bubble-ui';
import { AppError, capabilityUtils, Column, FakeReactNavigation, MOET_CONSTANTS, propUtils, } from 'moet-core';
import { generateCyElemId, isRunningContextCypress } from 'moet-test-utils/cypress/utils';
import { ExposeStateForCypress } from 'moet-test-utils/cypress/containers';
import { APP_PATHS, CAPS, FEATURES, HEADER_NO_CACHE, IS_ONBOARDING_APP, ONBOARDING_QUERY_PARAMS, REGIONS, IS_PSP_APP, } from './constants';
import capabilities from './selectors/capabilities';
import { noMid } from './actions/merchant';
import { hasAccess } from './selectors/utils';
import { getCorrelationIdFromWidgetCookie, getFrontendTypeFromQueryParam, hasInvalidFlowType, hasInvalidToken, isWidget, isDocumentUploadWidget, } from './selectors/widget';
import { hasLoadedDocumentTokenOnce } from './selectors/documentUpload';
import { isListDocumentsInitialized, isMerchantInitialized, isNoMid, isPreAssessmentFetched, isProductsInitialized, isValidatingWidget, } from './selectors/loadingStates';
import { ServerError, MoetFramework } from './components';
import { getIsValidWidgetToken, getKlarnaDotComProductsLink, getMerchant, getNeedsMerchantRefresh, getNeedsMerchantRefreshCount, getNeedsProductRefresh, getUserMerchantId, isManagedMerchant, isMerchantFound, isPreAssessmentMerchant, isNewSplitLayout, merchantRegion, } from './selectors/merchant';
import { getSelectedMid } from './selectors/midSelector';
import { isRecollectionMerchant, isRecollectionInitialized, } from './selectors/merchant/recollection';
import { getFeatureByKey } from './selectors/featuresConfig';
import createReducers from './reducers';
import Pricing from './pages/Pricing';
import ApplicationOverview from './pages/ApplicationOverview';
import translations from '../translations';
import { WhatsNext } from './containers/WhatsNext';
import { initApp } from './actions/signup';
import SupportFooter from './pages/App/SupportFooter';
import { MidSelectorPage, withGlobalMids, withMidChange } from './containers/MidSelector';
import { FreshChatWidget } from './components/FreshChat';
import FlowApp, { IS_BPC_APP } from './flow';
import { MoetGUI } from './moet-gui';
import { CY_FLOW_APP_CONTAINER_IDS } from './flow/constants';
import { getDecodedWidgetToken, getWidgetToken, getEncodedWidgetCookieFromParams, } from './utils/widget';
import { ALL_CAPS, getExecCaps, getInitCaps } from './caps';
import { pickProperties } from './utils/object';
import { ReCaptcha } from './components/ReCaptcha';
import { AnalyticsAndCookieSetup } from './components/AnalyticsAndCookieSetup';
import { initFriendbuy } from './utils/friendbuy';
import styled from 'styled-components';
import { PaymentServiceProviders } from '@/containers/PaymentServiceProviders';
import { getSupportedCountries } from '@/selectors/partners';
const { MANAGED_ONBOARDING_TYPES, WIDGET_TYPE } = MOET_CONSTANTS;
const encodedToken = getEncodedWidgetCookieFromParams();
const decodedWidgetToken = getDecodedWidgetToken();
const { type: env } = ((_b = (_a = window.config) === null || _a === void 0 ? void 0 : _a.common) === null || _b === void 0 ? void 0 : _b.environment) || {};
const FRONTEND_TYPE = decodedWidgetToken === null || decodedWidgetToken === void 0 ? void 0 : decodedWidgetToken.frontendType;
const WIDGET_TOKEN = getWidgetToken();
const IS_MOET_GUI = ['development', 'staging'].includes(env) && ((_c = window.location.pathname) === null || _c === void 0 ? void 0 : _c.startsWith('/products/gui'));
const appPath = 'steps';
const EXEC_CAPS = getExecCaps(FRONTEND_TYPE, IS_ONBOARDING_APP, IS_PSP_APP);
const INIT_CAPS = getInitCaps(FRONTEND_TYPE, IS_ONBOARDING_APP, IS_PSP_APP);
const ALL_CAPS_CATCHES = Object.values(ALL_CAPS).reduce((acc, curr) => (Object.assign(Object.assign({}, acc), { [curr.key]: curr.catch })), {});
const ALL_CAPS_PARSED = Object.values(ALL_CAPS).reduce((acc, curr) => (Object.assign(Object.assign({}, acc), { [curr.key]: curr.cap })), {});
let DID_CAPS_EXECUTE = false; // TODO fix re-rendering issue and remove this hack
const Container = styled.div.withConfig({ displayName: "Container", componentId: "sc-19epsev" }) `
  flex: '0 0 auto';
  margintop: ${isNewSplitLayout => (isNewSplitLayout ? 0 : 20)};
`;
const GrowingFlex = styled.div.withConfig({ displayName: "GrowingFlex", componentId: "sc-1d7x3y9" }) `
  position: 'relative';
  z-index: -1;
  flex: '1 0 auto';
`;
const App = ({ basePath, cypressRefreshMerchant = Promise.resolve, isNewSplitLayout, isRecollectionMerchant, merchantRegion, mid, showPspPage, }) => {
    if (isRunningContextCypress(window)) {
        // @ts-expect-error We need to disable this for Cypress, otherwise the application throws an error
        // and we can't recover from it
        window.ResizeObserver = null;
    }
    const enhancedProps = (props, basePath) => {
        return Object.assign(Object.assign({}, props), { basePath });
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(AnalyticsAndCookieSetup, null),
        React.createElement(Container, null,
            isRunningContextCypress(window) && (React.createElement(React.Fragment, null,
                React.createElement(FakeReactNavigation, { fieldId: generateCyElemId(CY_FLOW_APP_CONTAINER_IDS.INPUT_RELATIVE_PATH, window), buttonId: generateCyElemId(CY_FLOW_APP_CONTAINER_IDS.BTN_SUBMIT, window), onSubmit: () => {
                        return cypressRefreshMerchant({ params: { mid }, region: merchantRegion });
                    } }),
                React.createElement(ExposeStateForCypress, null))),
            React.createElement(Switch, null,
                showPspPage && (React.createElement(Route, { path: `${basePath}/${APP_PATHS.PSP_PATH}`, exact: true, 
                    // @ts-expect-error: FIXME
                    render: props => React.createElement(PaymentServiceProviders, Object.assign({}, props, { basePath: basePath })) })),
                !isRecollectionMerchant && (React.createElement(Route, { path: `${basePath}/${APP_PATHS.APPLICATION_OVERVIEW_PATH}`, exact: true, render: props => React.createElement(ApplicationOverview, Object.assign({}, enhancedProps(props, basePath))) })),
                React.createElement(Route, { path: `${basePath}/${APP_PATHS.PRICING_PATH}`, exact: true, render: props => React.createElement(Pricing, Object.assign({}, props, { basePath: basePath })) }),
                React.createElement(Route, { path: `${basePath}/${APP_PATHS.WHATS_NEXT_PATH}`, exact: true, render: props => React.createElement(WhatsNext, Object.assign({}, enhancedProps(props, basePath))) }),
                React.createElement(Route, { path: `${basePath}/${APP_PATHS.MID_SELECTOR}`, exact: true, render: props => React.createElement(MidSelectorPage, Object.assign({}, props, { basePath: basePath })) }),
                React.createElement(Route, { path: `${basePath}/${APP_PATHS.BPC_POC}/:${appPath}*`, exact: true, render: props => (React.createElement(FlowApp, Object.assign({}, props, { appPath: appPath, basePath: basePath, region: merchantRegion }))) }),
                React.createElement(Route, { path: `${basePath}/:${appPath}*`, render: props => (React.createElement(FlowApp, Object.assign({}, props, { appPath: appPath, basePath: basePath, region: merchantRegion }))) }),
                React.createElement(Route, { path: `/signup/:${appPath}*`, render: props => (React.createElement(FlowApp, Object.assign({}, props, { appPath: appPath, basePath: basePath, region: merchantRegion }))) }))),
        !isNewSplitLayout && (React.createElement(React.Fragment, null,
            React.createElement(GrowingFlex, null,
                React.createElement(SpacerVertical, { extraExtraLarge: true }),
                React.createElement(SpacerVertical, { medium: true }),
                React.createElement(SpacerVertical, { extraSmall: true })),
            React.createElement(GridContainer, null,
                React.createElement(SupportFooter, { merchantId: mid !== null && mid !== void 0 ? mid : '' }))))));
};
App.propTypes = {
    basePath: PropTypes.string.isRequired,
    isAppReady: PropTypes.bool.isRequired,
    isNewSplitLayout: PropTypes.bool.isRequired,
    isRecollectionMerchant: PropTypes.bool.isRequired,
    isWidget: PropTypes.bool.isRequired,
    merchant: PropTypes.object.isRequired,
    merchantRegion: PropTypes.oneOf(Object.values(REGIONS)).isRequired,
    cypressRefreshMerchant: PropTypes.func,
    mid: IS_ONBOARDING_APP || IS_PSP_APP ? PropTypes.string : PropTypes.string.isRequired,
    saveLanguage: PropTypes.func,
};
const WrappedApp = withRouter(compose(
// @ts-expect-error: FIXME
clientComponents.withPromiseBasedCapabilities({
    cypressRefreshMerchant: CAPS.V2_MERCHANTS_GET,
    validateWidgetToken: CAPS.V2_WIDGET_TOKEN_GET,
}), clientComponents.withCapabilities(ALL_CAPS_PARSED), 
// @ts-expect-error: FIXME
coreComponents.withTokenHelper(tokenHelper => ({ tokenHelper })), connect(
// @ts-expect-error: FIXME
(defaultState, { tokenHelper }) => {
    const state = defaultState;
    let isAppReady = false;
    if (IS_ONBOARDING_APP) {
        isAppReady = capabilities.hasFinished(INIT_CAPS)(state);
    }
    else if (isRecollectionMerchant(state)) {
        isAppReady =
            isRecollectionInitialized(state) && capabilities.hasFinished(INIT_CAPS)(state);
    }
    else if (isDocumentUploadWidget(state)) {
        isAppReady =
            capabilities.hasFinished(INIT_CAPS)(state) && hasLoadedDocumentTokenOnce(state);
    }
    else if (IS_BPC_APP) {
        isAppReady = capabilities.hasFinished(INIT_CAPS)(state);
    }
    else if (IS_PSP_APP) {
        const { country } = propUtils.getQueryParamsFromProps(window);
        if (capabilities.hasFinished(INIT_CAPS)(state) &&
            getSupportedCountries(country)(state) &&
            getFeatureByKey(FEATURES.PSP_PARTNERS)(state)) {
            isAppReady = true;
            window.parent.postMessage('showIframe', '*');
        }
        else {
            window.parent.postMessage('hideIframe', '*');
        }
    }
    else {
        isAppReady =
            (+isWidget(state) ^ +(getIsValidWidgetToken(state) === null)) === 1 && // added the + because of https://github.com/Microsoft/TypeScript/issues/587
                isMerchantInitialized(state) &&
                (isPreAssessmentMerchant(state) || isListDocumentsInitialized(state)) &&
                isProductsInitialized(state) &&
                capabilities.hasFinished(INIT_CAPS)(state) &&
                (!isPreAssessmentMerchant(state) || isPreAssessmentFetched(state));
    }
    return {
        basePath: `/${coreSelectors.slug(state)}`,
        designVersion: coreSelectors.getDesignVersion(state),
        getFrontendTypeFromQueryParam: getFrontendTypeFromQueryParam(state),
        hasAccess: hasAccess(state)(tokenHelper),
        hasInvalidToken: hasInvalidToken(state),
        hasInvalidFlowType: hasInvalidFlowType(state),
        correlationIdFromWidgetCookie: getCorrelationIdFromWidgetCookie(state),
        isAppReady: isAppReady || isNoMid(state),
        isChangingMid: !_isEmpty(getSelectedMid(state)),
        isManagedMerchant: isManagedMerchant(state),
        isMerchantFound: isMerchantFound(state),
        isNewSplitLayout: isNewSplitLayout(state),
        isPreAssessmentFetched: isPreAssessmentFetched(state),
        isPreAssessmentMerchant: isPreAssessmentMerchant(state),
        isValidWidgetToken: getIsValidWidgetToken(state),
        isWidget: isWidget(state),
        isDocumentUploadWidget: isDocumentUploadWidget(state),
        isRecollectionMerchant: isRecollectionMerchant(state),
        merchant: getMerchant(state),
        merchantRegion: merchantRegion(state),
        merchantRegionGetter: coreSelectors.getMerchantRegion(state),
        mid: getUserMerchantId(state),
        needsMerchantRefresh: getNeedsMerchantRefresh(state),
        needsMerchantRefreshCount: getNeedsMerchantRefreshCount(state),
        needsProductRefresh: getNeedsProductRefresh(state),
        productLink: getKlarnaDotComProductsLink(state),
        t: translator(state),
        isValidatingWidget: isValidatingWidget(state),
        showMaintenanceMode: getFeatureByKey(FEATURES.SHOW_PRODUCTS_MAINTENANCE_MODE)(state),
        showSignupMaintenanceMode: getFeatureByKey(FEATURES.SHOW_SIGNUP_MAINTENANCE_MODE)(state),
        showPspPage: getFeatureByKey(FEATURES.PSP_PARTNERS)(state),
    };
}, {
    noMid,
    setLanguage: languageKey => dispatch => {
        // @ts-expect-error: FIXME
        dispatch(i18nActions.language.set(languageKey));
    },
    initApp,
}), branch(({ mid, merchantRegionGetter, isWidget, isRecollectionMerchant, isDocumentUploadWidget, }) => {
    const merchantRegion = merchantRegionGetter(mid);
    // This condition is here to make sure that the merchant's onboarding region is already
    // available before rendering the app. The region is needed to make the initial requests to
    // the correct regional BEs and to avoid scenarios where a merchant sees data from another
    // merchant with the same mid from a different region.
    // See this incident: https://jira.int.klarna.net/jira/browse/INCIDENT-4769)
    // This applies only for MP sessions because for widget sessions we have the region already
    // provided as query param.
    return (!IS_ONBOARDING_APP &&
        !IS_PSP_APP &&
        !isWidget &&
        !isDocumentUploadWidget &&
        !IS_BPC_APP &&
        !isRecollectionMerchant &&
        !merchantRegion);
}, renderComponent(() => {
    return (
    // @ts-expect-error: FIXME
    React.createElement(Column, { alignItems: "center" },
        React.createElement(SpacerVertical, { extraExtraLarge: true }),
        React.createElement(Loader, null)));
})), withGlobalMids, withMidChange, withLifecycle({
    onWillMount(_a) {
        return __awaiter(this, arguments, void 0, function* ({ initApp, merchantRegion, validateWidgetToken, isValidatingWidget, isValidWidgetToken, history, }) {
            const queryParams = propUtils.getQueryParamsFromProps(window);
            if (IS_ONBOARDING_APP) {
                const mappedQueryParams = pickProperties(queryParams, ONBOARDING_QUERY_PARAMS);
                initApp(mappedQueryParams);
                history.replace(`/products/signup-gateway${location.search}`);
            }
            if (!isValidatingWidget &&
                !isValidWidgetToken &&
                [
                    WIDGET_TYPE.UNMANAGED_KYC,
                    WIDGET_TYPE.NATIVE_KYC,
                    WIDGET_TYPE.RECOLLECTION_KYC,
                    ...MANAGED_ONBOARDING_TYPES,
                ].includes(FRONTEND_TYPE)) {
                yield validateWidgetToken({ params: { token: WIDGET_TOKEN }, region: merchantRegion });
            }
        });
    },
    onDidMount(_a) {
        return __awaiter(this, void 0, void 0, function* () {
            var { getFrontendTypeFromQueryParam, isManagedMerchant, isPreAssessmentMerchant, isRecollectionMerchant, merchantRegion, mid, noMid, setLanguage } = _a, props = __rest(_a, ["getFrontendTypeFromQueryParam", "isManagedMerchant", "isPreAssessmentMerchant", "isRecollectionMerchant", "merchantRegion", "mid", "noMid", "setLanguage"]);
            // Both the BPC and the Recollection app are expected to not have mids
            if (!mid && !IS_ONBOARDING_APP && !IS_PSP_APP && !isRecollectionMerchant && !IS_BPC_APP) {
                noMid();
                return;
            }
            const params = { mid, frontend_type: undefined };
            if (isManagedMerchant) {
                params.frontend_type = getFrontendTypeFromQueryParam;
            }
            if (!DID_CAPS_EXECUTE) {
                DID_CAPS_EXECUTE = true;
                Object.keys(EXEC_CAPS).forEach(key => {
                    const cap = props[key];
                    if (cap) {
                        const promise = cap({ params, region: merchantRegion });
                        if (ALL_CAPS_CATCHES[key] !== undefined) {
                            promise.catch(ALL_CAPS_CATCHES[key]);
                        }
                    }
                });
            }
            if (isPreAssessmentMerchant) {
                // For pre-assessment collection we only show english strings.
                setLanguage('en');
            }
        });
    },
    onDidUpdate(prevProps, props) {
        const { needsMerchantRefresh: prevNeedsMerchantRefresh, needsProductRefresh: prevNeedsProductRefresh, location: prevLocation, } = prevProps;
        const { getMerchant, getProducts, location, merchantRegion, mid, needsMerchantRefresh, needsMerchantRefreshCount, needsProductRefresh, noMid, isDocumentUploadWidget, } = props;
        if (isDocumentUploadWidget) {
            // Refresh the docs
            // getRequiredDocuments({ params: { mid }, region: merchantRegion });
            // We return here so we don't even need to get merchant information
            return;
        }
        if (!prevNeedsMerchantRefresh && needsMerchantRefresh && needsMerchantRefreshCount <= 10) {
            mid !== null ? getMerchant({ params: { mid }, region: merchantRegion }) : noMid();
            getProducts({ params: { mid }, region: merchantRegion });
        }
        if (!prevNeedsProductRefresh && needsProductRefresh) {
            getProducts({ params: { mid }, region: merchantRegion });
        }
        if (location !== prevLocation) {
            window.scrollTo({ top: 0, left: 0 });
        }
    },
}), branch(({ isAppReady, isChangingMid }) => !isAppReady || isChangingMid, renderComponent(() => {
    return (
    // @ts-expect-error: FIXME
    React.createElement(Column, { alignItems: "center" },
        React.createElement(SpacerVertical, { extraExtraLarge: true }),
        React.createElement(Loader, null)));
})), branch(({ showMaintenanceMode }) => showMaintenanceMode, props => renderComponent(({ t }) => (React.createElement(AppError, { illustration: React.createElement(IllustrationErrorExplodingBalloon, null), title: t('products-fe.components.maintenance_mode.title.products_app'), message: t('products-fe.components.maintenance_mode.message') })))(props)), branch(({ showSignupMaintenanceMode }) => showSignupMaintenanceMode && IS_ONBOARDING_APP, props => renderComponent(({ t }) => (React.createElement(AppError, { illustration: React.createElement(IllustrationErrorExplodingBalloon, null), title: t('products-fe.components.maintenance_mode.title.signup_app'), message: t('products-fe.components.maintenance_mode.message') })))(props)), branch(
// @ts-expect-error: FIXME
props => !capabilityUtils.hasCapsAccess(ALL_CAPS_PARSED, props), props => {
    // eslint-disable-next-line no-console
    console.error('Requested capabilities not found or failed to load');
    return renderComponent(() => (React.createElement(AppError, { defaultTemplate: "internal_server_error", hasGoBack: false })))(props);
}), branch(({ hasInvalidToken }) => hasInvalidToken, props => {
    return renderComponent(({ t, correlationIdFromWidgetCookie, isPreAssessmentMerchant }) => {
        const message = isPreAssessmentMerchant ? '_risk' : '';
        return (React.createElement(AppError, { defaultTemplate: "forbidden", hasGoBack: false, title: t('products-fe.components.pre_assessment_error.title.invalid_token'), message: t(`products-fe.components.pre_assessment_error.message.invalid_token${message}`), correlationId: correlationIdFromWidgetCookie }));
    })(props);
}), branch(({ hasInvalidFlowType }) => hasInvalidFlowType, props => {
    return renderComponent(({ t, correlationIdFromWidgetCookie }) => (React.createElement(AppError, { hasGoBack: false, title: t('products-fe.components.pre_assessment_error.title.invalid_flow_type'), message: t('products-fe.components.pre_assessment_error.message.invalid_flow_type'), correlationId: correlationIdFromWidgetCookie })))(props);
}), branch(({ hasAccess }) => !IS_ONBOARDING_APP && !IS_PSP_APP && !hasAccess, props => {
    return renderComponent(() => React.createElement(AppError, { defaultTemplate: "forbidden", hasGoBack: false }))(props);
}), branch((props) => Object.keys(ALL_CAPS_PARSED).reduce((acc, key) => acc || !props[key], false), renderComponent(({ t }) => (React.createElement(AppError, { title: t('products-fe.components.temporarily_down.title'), message: t('products-fe.components.temporarily_down.message') })))), branch(({ isMerchantFound, needsMerchantRefreshCount }) => !IS_ONBOARDING_APP && !IS_PSP_APP && !isMerchantFound && needsMerchantRefreshCount > 10, renderComponent(() => React.createElement(AppError, { defaultTemplate: "forbidden" }))), branch(({ isValidWidgetToken }) => !IS_ONBOARDING_APP && !IS_PSP_APP && isValidWidgetToken === false, renderComponent(() => React.createElement(AppError, { defaultTemplate: "forbidden", hasGoBack: false }))), branch(({ mid }) => !IS_ONBOARDING_APP && !IS_PSP_APP && !mid && !IS_BPC_APP, renderComponent(() => React.createElement(AppError, { defaultTemplate: "forbidden", hasGoBack: false }))))(App));
const getApp = () => {
    if (IS_MOET_GUI)
        return MoetGUI;
    return WrappedApp;
};
const CapabilityWrappedApp = () => {
    const App = getApp();
    return (React.createElement("div", { id: "products-app" },
        React.createElement(clientComponents.CapabilitiesProvider, { AccessDeniedComponent: AccessDenied, desired: CAPS, ErrorComponent: () => React.createElement(ServerError, null), options: { headers: HEADER_NO_CACHE, params: { token: encodedToken } } },
            React.createElement(Router, null,
                IS_ONBOARDING_APP && !isRunningContextCypress(window) && (React.createElement(GoogleReCaptchaProvider, { reCaptchaKey: "6LeLFL4aAAAAANHFOwQiRdNIwXX7B5iVfFolOqW1", useEnterprise: true },
                    React.createElement(ReCaptcha, null),
                    React.createElement(App, null),
                    React.createElement(FreshChatWidget, null))),
                (!IS_ONBOARDING_APP || isRunningContextCypress(window)) && (React.createElement(React.Fragment, null,
                    React.createElement(App, null),
                    !isRunningContextCypress(window) && React.createElement(FreshChatWidget, null)))))));
};
// eslint-disable-next-line promise/catch-or-return, promise/always-return
initFriendbuy(document).then(() => {
    window.friendbuyAPI.push(['merchant', window.friendbuyAPI.merchantId]);
    window.friendbuyAPI.push(['track', 'page', { name: 'landed in sign-up flow' }, true]);
});
renderApp(CapabilityWrappedApp, {
    createReducers,
    translations,
    isPublic: IS_ONBOARDING_APP ||
        IS_PSP_APP ||
        IS_MOET_GUI ||
        [
            WIDGET_TYPE.UNMANAGED_KYC,
            WIDGET_TYPE.NATIVE_KYC,
            WIDGET_TYPE.RECOLLECTION_KYC,
            WIDGET_TYPE.DOCUMENT_UPLOAD,
            ...MANAGED_ONBOARDING_TYPES,
        ].includes(FRONTEND_TYPE),
    // @ts-expect-error: should be an MpUiFramework type
    Framework: MoetFramework,
});
